import React, { useState } from 'react';
import './GetInTouch.css';

export default function GetInTouch({ showDetails = true }) {
  const [formValues, setFormValues] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  }

  function isValidEmail(value) {
    return /.+@+.+\..+/.test(value);
  }

  function getError(name) {
    const v = formValues[name];
    if (!touched[name]) return '';
    if (!v || v.trim() === '') return 'Required';
    if (name === 'email' && !isValidEmail(v)) return 'Enter a valid email';
    if (name === 'phone' && v.replace(/\D/g, '').length < 10) return 'Enter a valid number';
    return '';
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ fullName: true, email: true, phone: true, message: true });

    const errors = [
      getError('fullName'),
      getError('email'),
      getError('phone'),
      getError('message'),
    ].filter(Boolean);

    if (errors.length > 0) {
      setStatus({ type: 'error', msg: 'Please fix the highlighted fields.' });
      return;
    }

    setIsLoading(true);
    setStatus(null);

    try {
      const SCRIPT_URL =
        'https://script.google.com/macros/s/AKfycbyQ5lZaEAYxnP85AXU-Z5LM_rJJlNWvU8qZEL9lvtN54NLuTtE1x2fGI7kGAIfUQLAB/exec';

      const params = new URLSearchParams({
        fullName: formValues.fullName,
        email: formValues.email,
        mobile: formValues.phone,
        message: formValues.message,
      });

      const response = await fetch(`${SCRIPT_URL}?${params.toString()}`, {
        method: 'GET',
        redirect: 'follow',
      });

      const result = await response.json();

      if (result.success) {
        setStatus({
          type: 'success',
          msg: 'Thank you! Your message has been sent successfully.',
        });
        setFormValues({ fullName: '', email: '', phone: '', message: '' });
        setTouched({});
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      setStatus({
        type: 'error',
        msg: 'There was an issue sending your message. Please email us directly at info@globstay.com',
      });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="git-section" id="get-in-touch">
      <div className="git-bg" aria-hidden="true" style={{ backgroundImage: "url('/get.jpg')" }} />
      <div className="git-accent-ring" aria-hidden="true" />
      <div className="git-accent-glow" aria-hidden="true" />

      <div className="git-container">
        <div className="git-content">
          {showDetails && (
            <div className="git-left">
              <span className="git-eyebrow">Get in touch</span>
              <h2 className="git-title">Book your private visit</h2>
              <p className="git-subtitle">
                Experience GlobStay in person. Our concierge will tailor a site tour
                and answer all your questions.
              </p>

              <div className="git-meta">
                <div className="git-meta-item">
                  <span className="git-meta-label">Email</span>
                  <a href="mailto:info@globstay.com" className="git-meta-value">
                    info@globstay.com
                  </a>
                </div>
                <div className="git-meta-item">
                  <span className="git-meta-label">Phone</span>
                  <a href="tel:+919999999999" className="git-meta-value">
                    +91 9999739944
                  </a>
                </div>
                <div className="git-meta-item">
                  <span className="git-meta-label">Visit us</span>
                  <span className="git-meta-value">Noida & Uttarakhand, India</span>
                </div>
              </div>
            </div>
          )}

          <div className="git-right">
            <form className="git-form" onSubmit={handleSubmit} noValidate>
              {/* Full name */}
              <div className={`git-field ${getError('fullName') ? 'git-field-error' : ''}`}>
                <input
                  className="git-input"
                  type="text"
                  name="fullName"
                  value={formValues.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder=" "
                  aria-label="Full name"
                  aria-invalid={!!getError('fullName')}
                  required
                  disabled={isLoading}
                />
                <label className="git-label">Full name</label>
                {getError('fullName') && (
                  <span className="git-error-text">{getError('fullName')}</span>
                )}
              </div>

              {/* Email */}
              <div className={`git-field ${getError('email') ? 'git-field-error' : ''}`}>
                <input
                  className="git-input"
                  type="email"
                  name="email"
                  value={formValues.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder=" "
                  aria-label="Email"
                  aria-invalid={!!getError('email')}
                  required
                  disabled={isLoading}
                />
                <label className="git-label">Email</label>
                {getError('email') && (
                  <span className="git-error-text">{getError('email')}</span>
                )}
              </div>

              {/* Phone */}
              <div className={`git-field ${getError('phone') ? 'git-field-error' : ''}`}>
                <input
                  className="git-input"
                  type="tel"
                  name="phone"
                  value={formValues.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder=" "
                  aria-label="Phone"
                  aria-invalid={!!getError('phone')}
                  required
                  disabled={isLoading}
                />
                <label className="git-label">Mobile number</label>
                {getError('phone') && (
                  <span className="git-error-text">{getError('phone')}</span>
                )}
              </div>

              {/* Message */}
              <div className={`git-field ${getError('message') ? 'git-field-error' : ''}`}>
                <textarea
                  className="git-input git-textarea"
                  name="message"
                  value={formValues.message}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder=" "
                  rows="4"
                  aria-label="Message"
                  aria-invalid={!!getError('message')}
                  required
                  disabled={isLoading}
                />
                <label className="git-label">Write your message</label>
                {getError('message') && (
                  <span className="git-error-text">{getError('message')}</span>
                )}
              </div>

              {/* Submit */}
              <button type="submit" className="git-submit" disabled={isLoading}>
                <span>{isLoading ? 'Sending...' : 'Submit request'}</span>
                {/* arrow icon removed per request */}
              </button>

              {status && (
                <div
                  className={`git-status ${
                    status.type === 'success'
                      ? 'git-status-success'
                      : 'git-status-error'
                  }`}
                  role="status"
                >
                  {status.msg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
